import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MousePointer2, X } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useProfile } from "../../hooks/queries";
import { updateCompletedTours, Profile } from "../../api/api";
import { TOURS, Tour, TourCtx, TourStep, findTour, resolveDyn } from "../../lib/tours";
import { tourEventSince } from "../../lib/tourEvents";
import { buildPracticeVacature, endPractice, isPracticeActive, startPractice } from "../../lib/practice";
import { toast } from "../../lib/toast";
import { cn } from "@/lib/utils";

// The HMP's "Hulp": a ? button (HelpButton) with a menu of topics, each a
// step-by-step tour that spotlights the part of the screen it explains
// (lib/tours.ts). Finished tours are stored on the user (PUT /profile/tours).

interface HelpContextValue {
  /** Help exists for this user (HMPs only). */
  enabled: boolean;
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  startTour: (id: string) => void;
  completed: string[];
  resetTours: () => void;
}

const HelpContext = createContext<HelpContextValue>({
  enabled: false,
  menuOpen: false,
  setMenuOpen: () => {},
  startTour: () => {},
  completed: [],
  resetTours: () => {},
});

export function useHelp() {
  return useContext(HelpContext);
}

interface Run {
  tourId: string;
  i: number;
  /** "Echt" was chosen at the practice/real choice. */
  real: boolean;
  /** The Oefenen option was chosen: lib/practice.ts is active. */
  practising: boolean;
  startedAt: number;
  stepAt: number;
}

interface Rect { x: number; y: number; w: number; h: number }

const POLL_MS = 150;
// A step's `need` may fail briefly while a page or dialog re-renders.
const NEED_GRACE_MS = 700;
// Let the next screen settle before evaluating the next step's `skip`.
const ADVANCE_DELAY_MS = 350;
// A click step whose result doesn't show up within this time shows its error.
const CLICK_ERROR_MS = 2500;
const TIP_WIDTH = 320;
// How long a step change waits for its target before centering the tooltip.
const SETTLE_MS = 600;
// Spotlight and tooltip moves between steps.
const MOVE = "350ms cubic-bezier(0.22, 1, 0.36, 1)";
const AUTO_START_KEY = "help.introOffered";

/** The first visible element marked data-tour="target". */
function findTarget(target: string): HTMLElement | null {
  const els = document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`);
  for (const el of els) {
    if (el.getClientRects().length > 0) return el;
  }
  return null;
}

function rectOf(el: HTMLElement): Rect | null {
  const b = el.getBoundingClientRect();
  if (!b.width || !b.height) return null;
  const top = Math.max(b.top, 8);
  const bottom = Math.min(b.bottom, window.innerHeight - 8);
  return { x: Math.round(b.left) - 6, y: Math.round(top) - 6, w: Math.round(b.width) + 12, h: Math.round(Math.max(bottom - top, 0)) + 12 };
}

function sameRect(a: Rect | null, b: Rect | null) {
  return a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h);
}

/** Scrolls el into view inside its scroll containers, if it isn't already. */
function reveal(el: HTMLElement) {
  const b = el.getBoundingClientRect();
  if (b.top < 80 || b.bottom > window.innerHeight - 80) el.scrollIntoView({ block: "center", behavior: "smooth" });
}

export function HelpProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const enabled = user?.role === "hmp";
  const { data: profile } = useProfile(enabled);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [run, setRun] = useState<Run | null>(null);
  // The spotlight, and the step ("tourId:i") it was measured for. On a step
  // change it keeps the previous rect until the new target is found, so the
  // spotlight glides from one target to the next instead of blinking out.
  const [spot, setSpot] = useState<{ rect: Rect | null; key: string | null }>({ rect: null, key: null });
  const [err, setErr] = useState<string | null>(null);
  // The target hasn't shown up for a while: offer Volgende anyway.
  const [stuck, setStuck] = useState(false);

  const runRef = useRef<Run | null>(null);
  runRef.current = run;
  const spotRef = useRef(spot);
  spotRef.current = spot;
  const pendingRef = useRef(false);
  const needFailSince = useRef<number | null>(null);
  const clickedAt = useRef<number | null>(null);
  const revealedFor = useRef<string | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const completed = useMemo(() => profile?.completed_tours ?? [], [profile]);
  const completedRef = useRef(completed);
  completedRef.current = completed;

  const vacaturesPath = `/vacatures/${user?.partner_id ?? ""}`;
  const contacts = profile?.waves_contacts ?? [];
  const contact = contacts.length > 0 ? contacts.map(c => c.name).join(" & ") : "je contactpersoon bij Waves";

  const ctxFor = useCallback((r: Run): TourCtx => ({
    path: window.location.pathname,
    real: r.real,
    has: t => !!findTarget(t),
    value: t => (findTarget(t)?.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea")?.value ?? "").trim(),
    text: t => findTarget(t)?.textContent ?? "",
    count: t => Number(findTarget(t)?.dataset.tourCount ?? 0),
    checked: t => !!findTarget(t)?.querySelector('[data-state="checked"]'),
    since: e => tourEventSince(e, r.stepAt),
    sinceStart: e => tourEventSince(e, r.startedAt),
    vacaturesPath,
    contact,
  }), [vacaturesPath, contact]);

  const saveCompleted = useCallback(async (ids: string[]) => {
    try {
      const res = await updateCompletedTours(ids, { silenceErrors: true });
      queryClient.setQueryData<Profile>(["profile"], res);
    } catch {
      // Progress is a convenience; a failed save just leaves the ✓ off.
    }
  }, [queryClient]);

  const endRun = useCallback((r: Run) => {
    setRun(null);
    setSpot({ rect: null, key: null });
    setErr(null);
    setStuck(false);
    pendingRef.current = false;
    clickedAt.current = null;
    if (r.practising || isPracticeActive()) {
      endPractice();
      queryClient.invalidateQueries({ queryKey: ["vacatures"] });
      queryClient.invalidateQueries({ queryKey: ["vacature"] });
      if (/^\/vacature\/-\d+$/.test(window.location.pathname)) navigate(vacaturesPath);
    }
  }, [queryClient, navigate, vacaturesPath]);

  const finish = useCallback((r: Run) => {
    const tour = findTour(r.tourId);
    endRun(r);
    if (!tour) return;
    if (!completedRef.current.includes(tour.id)) void saveCompleted([...completedRef.current, tour.id]);
    toast(`${tour.label} afgerond${r.practising ? " · oefening verwijderd" : ""}`, { kind: "success" });
  }, [endRun, saveCompleted]);

  const startTour = useCallback((id: string) => {
    const tour = findTour(id);
    if (!tour) return;
    if (runRef.current) endRun(runRef.current);
    setMenuOpen(false);
    const now = Date.now();
    const r: Run = { tourId: id, i: -1, real: false, practising: false, startedAt: now, stepAt: now };
    goToRef.current(r, 0, 1);
  }, [endRun]);

  const abort = useCallback((r: Run) => {
    const tour = findTour(r.tourId);
    endRun(r);
    if (tour) toast(`Rondleiding gestopt · ${tour.label}`, { action: { label: "Opnieuw starten", onClick: () => startTour(tour.id) }, durationMs: 8000 });
  }, [endRun, startTour]);

  /** Moves to step i (skipping steps whose `skip` holds, in direction dir), or finishes past the end. */
  function goTo(r: Run, i: number, dir: 1 | -1) {
    const tour = findTour(r.tourId);
    if (!tour) return;
    const c = ctxFor(r);
    while (i >= 0 && i < tour.steps.length && tour.steps[i].skip?.(c)) i += dir;
    if (i < 0) i = 0;
    if (i >= tour.steps.length) { finish(r); return; }
    pendingRef.current = false;
    needFailSince.current = null;
    clickedAt.current = null;
    revealedFor.current = null;
    setErr(null);
    setStuck(false);
    // A centered step: the spotlight closes up to the middle of the screen.
    if (!tour.steps[i].target) setSpot({ rect: null, key: `${r.tourId}:${i}` });
    const go = tour.steps[i].go;
    if (go && window.location.pathname !== go) navigate(go);
    setRun({ ...r, i, stepAt: Date.now() });
  }

  const goToRef = useRef(goTo);
  goToRef.current = goTo;

  function advanceSoon(r: Run) {
    pendingRef.current = true;
    setTimeout(() => {
      const cur = runRef.current;
      if (!cur || cur.tourId !== r.tourId || cur.i !== r.i) return;
      goTo(cur, cur.i + 1, 1);
    }, ADVANCE_DELAY_MS);
  }

  function currentStep(r: Run | null): { tour: Tour; step: TourStep } | null {
    const tour = r && findTour(r.tourId);
    return tour && r && tour.steps[r.i] ? { tour, step: tour.steps[r.i] } : null;
  }

  // ── The step watcher ───────────────────────────────────────
  useEffect(() => {
    if (!run) return;
    const iv = setInterval(() => {
      const r = runRef.current;
      const cur = currentStep(r);
      if (!r || !cur || pendingRef.current) return;
      const { step } = cur;
      const c = ctxFor(r);

      if (step.until?.(c)) { advanceSoon(r); return; }
      if (step.back?.(c)) { goTo(r, r.i - 1, -1); return; }
      if (step.need && !step.need(c)) {
        needFailSince.current ??= Date.now();
        if (Date.now() - needFailSince.current > NEED_GRACE_MS) {
          if (step.ok?.(c)) goTo(r, r.i + 1, 1);
          else abort(r);
        }
        return;
      }
      needFailSince.current = null;

      if (step.click && clickedAt.current && Date.now() - clickedAt.current > CLICK_ERROR_MS) {
        clickedAt.current = null;
        setErr(resolveDyn(step.err ?? "Dat lukte nog niet. Probeer het opnieuw.", c));
      }

      const el = step.target ? findTarget(step.target) : null;
      const key = `${r.tourId}:${r.i}`;
      if (el && revealedFor.current !== key) {
        revealedFor.current = key;
        reveal(el);
      }
      const found = spotRef.current.key === key && !!spotRef.current.rect;
      if (step.target && !found && Date.now() - r.stepAt > 2000) setStuck(true);
      else if (found) setStuck(false);
    }, POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.tourId, run?.i, ctxFor]);

  // The spotlight follows its target every frame, so it stays on it while
  // the page scrolls or a dialog animates in.
  useEffect(() => {
    if (!run) return;
    let frame = 0;
    const track = () => {
      const r = runRef.current;
      const step = r && currentStep(r)?.step;
      if (r && step?.target) {
        const key = `${r.tourId}:${r.i}`;
        const el = findTarget(step.target);
        const next = el ? rectOf(el) : null;
        const cur = spotRef.current;
        // Until the new target shows up, keep the previous step's spotlight;
        // a target that disappears mid-step closes the spotlight.
        if (next ? cur.key !== key || !sameRect(next, cur.rect) : cur.key === key && !!cur.rect) {
          setSpot({ rect: next, key });
        }
      }
      frame = requestAnimationFrame(track);
    };
    frame = requestAnimationFrame(track);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.tourId, run?.i]);

  // Lets dropdowns open above the tour overlay (styles/globals.css).
  const running = !!run;
  useEffect(() => {
    if (!running) return;
    document.documentElement.setAttribute("data-tour-active", "");
    return () => document.documentElement.removeAttribute("data-tour-active");
  }, [running]);

  // Click steps: a click on the target continues (or, with `until`, lets the watcher continue).
  useEffect(() => {
    if (!run) return;
    function onClick(e: MouseEvent) {
      const r = runRef.current;
      const cur = currentStep(r);
      if (!r || !cur || !cur.step.click || !cur.step.target) return;
      const el = e.target as Element | null;
      if (!el?.closest || el.closest("[data-tour-ui]")) return;
      if (!el.closest(`[data-tour="${cur.step.target}"]`)) return;
      setErr(null);
      if (cur.step.until) { clickedAt.current = Date.now(); return; }
      const step = cur.step;
      setTimeout(() => {
        const now = runRef.current;
        if (!now || now.tourId !== r.tourId || now.i !== r.i) return;
        const c = ctxFor(now);
        if (step.ok && !step.ok(c)) { setErr(resolveDyn(step.err ?? "Dat lukte nog niet. Probeer het opnieuw.", c)); return; }
        goTo(now, now.i + 1, 1);
      }, 320);
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.tourId, run?.i, ctxFor]);

  // Escape stops the tour (or closes the menu).
  useEffect(() => {
    if (!run && !menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (runRef.current) endRun(runRef.current);
      else setMenuOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run, menuOpen, endRun]);

  // A logout or role change ends any tour.
  useEffect(() => {
    if (!enabled && runRef.current) endRun(runRef.current);
  }, [enabled, endRun]);

  // First visit: offer the intro tour once per browser session until it's finished.
  useEffect(() => {
    if (!enabled || !profile || run || location.pathname !== "/") return;
    if (profile.completed_tours.includes("intro")) return;
    try {
      if (sessionStorage.getItem(AUTO_START_KEY)) return;
      sessionStorage.setItem(AUTO_START_KEY, "1");
    } catch {
      return;
    }
    const t = setTimeout(() => startTour("intro"), 800);
    return () => clearTimeout(t);
  }, [enabled, profile, run, location.pathname, startTour]);

  // ── Actions from the tooltip ───────────────────────────────

  function next() {
    const r = runRef.current;
    const cur = currentStep(r);
    if (!r || !cur) return;
    const c = ctxFor(r);
    if (cur.step.ok && !cur.step.ok(c)) { setErr(resolveDyn(cur.step.err ?? "Dat lukte nog niet. Probeer het opnieuw.", c)); return; }
    goTo(r, r.i + 1, 1);
  }

  function prevIndex(r: Run): number {
    const cur = currentStep(r);
    if (!cur || cur.step.noPrev) return -1;
    const c = ctxFor(r);
    for (let j = r.i - 1; j >= 0; j--) {
      const s = cur.tour.steps[j];
      if (s.click || s.act || s.choice) return -1;
      if (!s.skip?.(c)) return j;
    }
    return -1;
  }

  function prev() {
    const r = runRef.current;
    if (!r) return;
    const j = prevIndex(r);
    if (j >= 0) goTo(r, j, -1);
  }

  function choose(real: boolean) {
    const r = runRef.current;
    const tour = r && findTour(r.tourId);
    if (!r || !tour) return;
    const practising = !real && !!tour.practice;
    if (practising) {
      if (tour.practice === "create") startPractice({ captureCreates: true });
      else startPractice({ vacature: buildPracticeVacature(user?.partner_id ?? null, user?.branch_permissions?.[0] ?? null, user?.name ?? "") });
      queryClient.invalidateQueries({ queryKey: ["vacatures"] });
    }
    goTo({ ...r, real, practising }, r.i + 1, 1);
  }

  const resetTours = useCallback(() => {
    void saveCompleted([]);
    toast("Alle rondleidingen staan weer klaar", { kind: "success" });
  }, [saveCompleted]);

  const value = useMemo<HelpContextValue>(
    () => ({ enabled, menuOpen, setMenuOpen, startTour, completed, resetTours }),
    [enabled, menuOpen, startTour, completed, resetTours],
  );

  return (
    <HelpContext.Provider value={value}>
      {children}
      {enabled && run && (
        <TourOverlay
          run={run}
          rect={spot.rect}
          rectIsCurrent={spot.key === `${run.tourId}:${run.i}` && !!spot.rect}
          err={err}
          stuck={stuck}
          ctx={ctxFor(run)}
          tipRef={tipRef}
          hasPrev={prevIndex(run) >= 0}
          onNext={next}
          onPrev={prev}
          onStop={() => endRun(run)}
          onChoose={choose}
        />
      )}
    </HelpContext.Provider>
  );
}

// ── Spotlight + tooltip ──────────────────────────────────────

function TourOverlay({
  run, rect, rectIsCurrent, err, stuck, ctx, tipRef, hasPrev, onNext, onPrev, onStop, onChoose,
}: {
  run: Run;
  /** The spotlight: the current step's target, or (briefly, on a step change) the previous one. */
  rect: Rect | null;
  rectIsCurrent: boolean;
  err: string | null;
  stuck: boolean;
  ctx: TourCtx;
  tipRef: React.RefObject<HTMLDivElement>;
  hasPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
  onStop: () => void;
  onChoose: (real: boolean) => void;
}) {
  const tour = findTour(run.tourId)!;
  const step = tour.steps[run.i];
  const n = tour.steps.length;
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  // Animate moves only after the first placement, so the tooltip doesn't
  // fly in from off-screen when the tour starts.
  const [placed, setPlaced] = useState(false);
  // A step whose target hasn't shown up yet waits briefly before the tooltip
  // gives up on it and moves to the center.
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    setSettled(false);
    const t = setTimeout(() => setSettled(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [run.tourId, run.i]);
  useEffect(() => {
    if (!pos || placed) return;
    const frame = requestAnimationFrame(() => setPlaced(true));
    return () => cancelAnimationFrame(frame);
  }, [pos, placed]);

  useLayoutEffect(() => {
    // Stay put while the new step's target is still being looked for.
    if (step.target && !rectIsCurrent && !settled) return;
    const vw = window.innerWidth, vh = window.innerHeight;
    const W = Math.min(TIP_WIDTH, vw - 32);
    const H = tipRef.current?.offsetHeight ?? 220;
    let x = (vw - W) / 2, y = (vh - H) / 2;
    if (rect && rectIsCurrent) {
      const below = rect.y + rect.h + 14, above = rect.y - H - 14, right = rect.x + rect.w + 14;
      if (step.place === "above" && above > 12) { y = above; x = rect.x; }
      else if (below + H < vh) { y = below; x = rect.x; }
      else if (above > 12) { y = above; x = rect.x; }
      else if (right + W < vw) { x = right; y = rect.y; }
      else { x = rect.x - W - 14; y = rect.y; }
      x = Math.max(16, Math.min(vw - W - 16, x));
      y = Math.max(16, Math.min(vh - H - 16, y));
    }
    setPos(p => (p && p.x === Math.round(x) && p.y === Math.round(y) ? p : { x: Math.round(x), y: Math.round(y) }));
  });

  // Keyboard users land in the tooltip on every step.
  useEffect(() => {
    const t = setTimeout(() => {
      const tip = tipRef.current;
      if (tip && !tip.contains(document.activeElement) && !document.activeElement?.closest("input, textarea, [role=dialog]")) tip.focus({ preventScroll: true });
    }, 60);
    return () => clearTimeout(t);
  }, [run.i, tipRef]);

  const waitsForUser = !!(step.click || step.act);
  const showNext = !step.choice && (!waitsForUser || stuck);
  const last = !tour.steps.slice(run.i + 1).some(s => !s.skip?.(ctx));
  const title = resolveDyn(step.title, ctx);
  const text = resolveDyn(step.text, ctx) + (step.target && !rectIsCurrent && settled ? (stuck ? " Dit onderdeel staat nu niet in beeld." : " Even geduld, dit onderdeel komt zo in beeld.") : "");
  const showPracticeNote = run.practising && run.i === tour.steps.findIndex(s => s.target === "vac-practice");

  return createPortal(
    <>
      {/* One spotlight for the whole tour: it glides from target to target,
          and closes up to the middle of the screen for a centered step. */}
      <div
        aria-hidden
        className="pointer-events-none fixed z-[200] rounded-[14px] duration-300 animate-in fade-in-0"
        style={{
          left: rect ? rect.x : window.innerWidth / 2,
          top: rect ? rect.y : window.innerHeight / 2,
          width: rect ? rect.w : 0,
          height: rect ? rect.h : 0,
          boxShadow: `0 0 0 3px ${rect ? "rgba(61,184,245,1)" : "rgba(61,184,245,0)"}, 0 0 0 9999px rgba(16,32,47,0.55)`,
          transition: `left ${MOVE}, top ${MOVE}, width ${MOVE}, height ${MOVE}, box-shadow ${MOVE}`,
        }}
      />
      <div
        ref={tipRef}
        data-tour-ui
        tabIndex={-1}
        role="dialog"
        aria-label={`Rondleiding: ${tour.label}`}
        aria-live="polite"
        className="pointer-events-auto fixed z-[201] flex flex-col overflow-hidden rounded-[18px] bg-white text-waves-ink shadow-modal outline-none"
        style={{
          left: pos?.x ?? -9999,
          top: pos?.y ?? -9999,
          width: Math.min(TIP_WIDTH, window.innerWidth - 32),
          opacity: pos ? 1 : 0,
          transition: placed ? `left ${MOVE}, top ${MOVE}, opacity 200ms ease-out` : "opacity 200ms ease-out",
        }}
      >
        <div className="h-1 bg-waves-line">
          <div className="h-full bg-waves-zee transition-[width] duration-300" style={{ width: `${Math.round(((run.i + 1) / n) * 100)}%` }} />
        </div>
        <div key={run.i} className="flex flex-col gap-2 px-5 pb-[18px] pt-4 duration-300 animate-in fade-in-0">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-xs text-waves-muted">{tour.label}</span>
            <span className="font-mono text-xs text-waves-muted">{run.i + 1}/{n}</span>
            <button
              type="button"
              onClick={onStop}
              aria-label="Rondleiding stoppen"
              title="Stoppen"
              className="flex h-[26px] w-[26px] items-center justify-center rounded-pill text-waves-muted hover:bg-waves-mist hover:text-waves-ink"
            >
              <X size={14} />
            </button>
          </div>
          {!step.click && <div className="text-lg font-black leading-tight tracking-[-0.01em]">{title}</div>}
          <div className={cn("whitespace-pre-line leading-normal text-pretty", waitsForUser ? "text-base text-waves-ink" : "text-sm text-waves-body")}>{text}</div>
          {showPracticeNote && (
            <div className="text-xs leading-snug text-status-warn-fg">Je oefent met een oefenvacature. Die verdwijnt na de rondleiding.</div>
          )}
          {step.click && rectIsCurrent && !err && (
            <div className="flex items-center gap-2 pt-1.5 text-[13px] font-bold text-waves-accent">
              <MousePointer2 size={16} /> Klik op het onderdeel
            </div>
          )}
          {err && (
            <div className="flex items-center gap-2 pt-1.5 text-[13px] font-bold text-status-alert-fg">
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-alert-bg text-[11px]">!</span>
              {err}
            </div>
          )}
          {step.choice && (
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => onChoose(false)} className="rounded-pill border border-waves-line bg-white px-4 py-[9px] text-[13px] font-bold hover:bg-waves-mist">
                Oefenen
              </button>
              <button type="button" onClick={() => onChoose(true)} className="rounded-pill bg-waves-koraal px-4 py-[9px] text-[13px] font-bold text-white hover:bg-waves-koraal-hover">
                {tour.realLabel ?? "Echte vacature"}
              </button>
            </div>
          )}
          {(showNext || hasPrev) && (
            <div className="flex justify-end gap-2 pt-2">
              {hasPrev && (
                <button type="button" onClick={onPrev} className="rounded-pill border border-waves-line bg-white px-3.5 py-2 text-[13px] font-bold hover:bg-waves-mist">
                  Vorige
                </button>
              )}
              {showNext && (
                <button type="button" onClick={onNext} className="rounded-pill bg-waves-koraal px-4 py-[9px] text-[13px] font-bold text-white hover:bg-waves-koraal-hover">
                  {last ? "Klaar" : "Volgende"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export const TOUR_COUNT = TOURS.length;
